import type { ChartConfig } from "@/shared/ui/chart";

// ===========================================================================
// auth
// ===========================================================================

export const AUTH_STORAGE_KEY = "authenticate";
export const AUTH_COOKIE_KEY = "pharmacy_auth_present";
// Bearer-token fallback for browsers that block third-party cookies (iOS Safari,
// iOS Chrome via WebKit, Firefox ETP Strict, Chrome with 3P cookies disabled).
// The API also sets the same token in an HttpOnly cookie; the bearer path is
// just the fallback when the cookie cannot be sent cross-site.
export const AUTH_TOKEN_STORAGE_KEY = "pharmacy_auth_token";

// ===========================================================================
// organizations
// ===========================================================================

export const ORGANIZATIONS_LIMIT = 100;
export const VENDORS_LIMIT = 1000;
export const CERTIFIED_MEDICINES_LIMIT = 25;
export const CERTIFIED_MEDICINES_MIN_YEAR = 2013;
export const REFERENT_PRICES_LIMIT = 25;
export const REFERENT_PRICE_CURRENCIES = ["UZS", "USD", "EUR", "RUB"] as const;
export type ReferentPriceCurrency = (typeof REFERENT_PRICE_CURRENCIES)[number];
export const PRICE_LIST_FILES_PAGE_SIZE = 9;
export const REGIONS = [
  "tashkent",
  "andijon",
  "bukhara",
  "fergana",
  "jizzakh",
  "karakalpakstan",
  "namangan",
  "navoiy",
  "qashqadaryo",
  "samarqand",
  "sirdaryo",
  "surxondaryo",
  "xorazm",
] as const;

export type Region = (typeof REGIONS)[number];

export const ORGANIZATION_REGIONS = [
  "andijon",
  "buxoro",
  "fargona",
  "jizzax",
  "qashqadaryo",
  "qoraqalpogiston",
  "namangan",
  "navoiy",
  "samarqand",
  "sirdaryo",
  "surxondaryo",
  "toshkent",
  "toshkent_viloyati",
  "xorazm",
] as const;

export type OrganizationRegion = (typeof ORGANIZATION_REGIONS)[number];

export const ORGANIZATION_TYPES = ["retail", "wholesale"] as const;

export type OrganizationType = (typeof ORGANIZATION_TYPES)[number];

export const MEDICINE_PERIOD_OPTIONS = ["7d", "14d", "30d", "90d"] as const;

export const MEDICINE_PRICE_CHART_CONFIG = {
  avgPrice: { label: "Average price", color: "var(--chart-1)" },
  minPrice: { label: "Minimum price", color: "var(--chart-2)" },
  maxPrice: { label: "Maximum price", color: "var(--chart-3)" },
} satisfies ChartConfig;

export const EXCEL_SERIAL_DATE_EPOCH_UTC = Date.UTC(1899, 11, 30);

export const SEARCH_NORMALIZER_MAP: Record<string, string> = {
  а: "a",
  б: "b",
  в: "v",
  г: "g",
  д: "d",
  е: "e",
  ё: "yo",
  ж: "zh",
  з: "z",
  и: "i",
  й: "y",
  к: "k",
  л: "l",
  м: "m",
  н: "n",
  о: "o",
  п: "p",
  р: "r",
  с: "s",
  т: "t",
  у: "u",
  ф: "f",
  х: "x",
  ц: "ts",
  ч: "ch",
  ш: "sh",
  щ: "sh",
  ъ: "",
  ы: "i",
  ь: "",
  э: "e",
  ю: "yu",
  я: "ya",
  қ: "q",
  ғ: "g",
  ҳ: "h",
  ў: "o",
};

// ===========================================================================
// medicines
// ===========================================================================

export const PERIOD_OPTIONS = ["7d", "14d", "30d", "90d"] as const;
export const VARIANT_ATTRIBUTES = [
  "dosage_form",
  "strength",
  "volume",
  "container_type",
  "pack",
  "vendor_country",
] as const;

export type VariantAttribute = (typeof VARIANT_ATTRIBUTES)[number];

export const DOSAGE_FORM_ICONS: Record<string, string> = {
  tablet: "💊",
  capsule: "💊",
  syrup: "🧴",
  solution: "💧",
  suspension: "💧",
  drops: "💧",
  injection: "💉",
  infusion: "💉",
  powder: "🧂",
  granule: "🧂",
  ointment: "🧴",
  cream: "🧴",
  gel: "🧴",
  lotion: "🧴",
  inhaler: "🌬",
  aerosol: "🌬",
  spray: "🌬",
  suppository: "💊",
  pessary: "💊",
  enema: "💧",
  patch: "🩹",
  paste: "🧴",
  pastille: "🍬",
};

export const PRICE_CHART_CONFIG = {
  avgPrice: { label: "Average price", color: "var(--chart-1)" },
  minPrice: { label: "Minimum price", color: "var(--chart-2)" },
  maxPrice: { label: "Maximum price", color: "var(--chart-3)" },
} satisfies ChartConfig;

// ===========================================================================
// data-table cells
// ===========================================================================

export const KNOWN_DOSAGE_FORMS = new Set([
  "tablet",
  "capsule",
  "syrup",
  "solution",
  "suspension",
  "drops",
  "injection",
  "infusion",
  "powder",
  "granule",
  "ointment",
  "cream",
  "gel",
  "lotion",
  "inhaler",
  "aerosol",
  "spray",
  "suppository",
  "pessary",
  "enema",
  "patch",
  "paste",
  "pastille",
]);

/**
 * Maps free-form unit suffixes (Latin and Cyrillic) to canonical i18n keys
 * under `medicines.units`.
 */
export const UNIT_ALIASES: Record<string, string> = {
  mg: "mg",
  мг: "mg",
  mkg: "mcg",
  mcg: "mcg",
  мкг: "mcg",
  µg: "mcg",
  ug: "mcg",
  g: "g",
  г: "g",
  kg: "kg",
  кг: "kg",
  ml: "ml",
  мл: "ml",
  l: "l",
  л: "l",
  iu: "iu",
  ме: "iu",
  xb: "iu",
  "%": "percent",
};
